import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Input, Label, Pagination, PaginationItem, PaginationLink,
  Modal, ModalHeader, ModalBody, ModalFooter, Spinner, FormGroup
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import FileAssignModal from "../../../Components/Common/Modal/FileAssignModal";
import debounce from "lodash.debounce";
import Select from "react-select";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";

import { getAuthUser } from "../../../helpers/utils";
import * as url from "../../../helpers/url_helper";
import { fetchUsers } from "../../../slices/thunks";
import { updateCaseJson } from "../../../slices/cases/thunk";

const hideFields = {
  judgments: [
    "parties",
    "procedural_history",
    "text_segments",
    "principles", 
    "rule_of_law",
    "full_judgment_text",
    "case_identifiers",
    "legal_provisions_relied_upon",
    "type_specific",
    "extra_metadata"

  ]
};


/* ──────────────── Reusable Components ──────────────── */
// export function RecursiveEditor ({ value = '', path = [], onChange, name, keys, readOnly = false }) {
//   const { t } = useTranslation();
//   const isObject = value && typeof value === "object" && !Array.isArray(value);
//   const isArray = Array.isArray(value);

//   const handlePrimitiveChange = (newVal) => {
//     onChange(path, newVal);
//   };


//   if (isObject) {
//     const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);
//     return (
//       <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
//         {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
//         {objectKeys.map((key) => (
//           <RecursiveEditor
//             key={key}
//             keys={keys}
//             name={key}
//             value={value[key]}
//             path={[...path, key]}
//             onChange={onChange}
//             readOnly={readOnly}
//           />
//         ))}
//       </div>
//     );
//   }

//   else if (isArray) {
//     // 🧱 Default array rendering (non-"articles")
//     return (
//       <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
//         <div style={{ fontWeight: 600, marginBottom: 6 }}>
//           {t(name)} ({t('length')}: {value.length})
//         </div>
//         {value.map((item, idx) => (
//           <div key={idx} style={{ marginBottom: 8 }}>
//             <RecursiveEditor
//               name={`[${idx}]`}
//               value={item}
//               path={[...path, idx]}
//               onChange={onChange}
//               keys={keys}
//               readOnly={readOnly}
//             />
//             {!readOnly && (
//               <div>
//                 <Button
//                   size="sm"
//                   color="danger"
//                   outline
//                   onClick={() => {
//                     const newArr = [...value];
//                     newArr.splice(idx, 1);
//                     onChange(path, newArr);
//                   }}
//                 >
//                   {t('Remove')}
//                 </Button>
//               </div>
//             )}
//           </div>
//         ))}
//         {!readOnly && (
//           <div style={{ marginTop: 6 }}>
//             <Button
//               size="sm"
//               color="primary"
//               outline
//               onClick={() => {
//                 const newArr = [...value, typeof value[0] === "object" ? {} : ""];
//                 onChange(path, newArr);
//               }}
//             >
//               {t('Add')}
//             </Button>
//           </div>
//         )}
//       </div>
//     );
//   }

//   else {
//     const asString = value === null ? "" : String(value);
//     const useTextarea = asString.length > 60 || asString.includes("\n");

//     return (
//       <FormGroup style={{ marginBottom: 8 }}>
//         <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
//         {useTextarea ? (
//           <Input
//             type="textarea"
//             value={asString}
//             rows={5}
//             onChange={(e) => handlePrimitiveChange(e.target.value)}
//             readOnly={readOnly}
//           />
//         ) : (
//           <Input
//             type="text"
//             value={asString}
//             onChange={(e) => handlePrimitiveChange(e.target.value)}
//             readOnly={readOnly}
//           />
//         )}
//       </FormGroup>
//     );
//   }
// }

export function RecursiveEditor({
  value = "",
  path = [],
  onChange,
  name,
  keys,
  readOnly = false,
}) {
  const { t } = useTranslation();
  // use pagination only for array of objects
  const [page, setPage] = useState(0);
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };

  if (isObject) {
    const objectKeys = keys
      ? Object.keys(value).filter((k) => !keys.includes(k))
      : Object.keys(value);
    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            keys={keys}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  }

  else if (isArray) {
    const isArrayOfObjects =
      value.length > 0 && value.every((item) => typeof item === "object" && item !== null);



    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {t(name)} ({t("length")}: {value.length})
          </div>

          {(!readOnly && isArrayOfObjects) && (
            <div style={{ marginTop: 6 }}>
              <Button
                size="sm"
                color="primary"
                outline
                onClick={() => {
                  let newItem;

                  if (typeof value[0] === "object" && value[0] !== null && !Array.isArray(value[0])) {
                    // clone object structure with empty string values
                    newItem = Object.fromEntries(Object.keys(value[0]).map((key) => [key, ""]));
                  } else {
                    // fallback for primitive array (string, number, etc.)
                    newItem = "";
                  }

                  const newArr = [...value, newItem];
                  onChange(path, newArr);
                }}
              >
                {t("Add")}
              </Button>
            </div>
          )}
        </div>


        {isArrayOfObjects ? (
          <>
            <RecursiveEditor
              name={`[${page}]`}
              value={value[page]}
              path={[...path, page]}
              onChange={onChange}
              keys={keys}
              readOnly={readOnly}
            />

            {!readOnly && (
              <div style={{ marginTop: 8 }}>
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => {
                    const newArr = [...value];
                    newArr.splice(page, 1);
                    onChange(path, newArr);
                    if (page >= newArr.length) setPage(Math.max(0, newArr.length - 1));
                  }}
                >
                  {t("Remove")}
                </Button>
              </div>
            )}

            {value.length > 1 && (
              <div style={{ marginTop: 10 }}>
                <PaginationControl
                  total={value.length}
                  current={page}
                  onChange={(p) => {
                    setPage(p);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <>
            {value.map((item, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <RecursiveEditor
                  name={`[${idx}]`}
                  value={item}
                  path={[...path, idx]}
                  onChange={onChange}
                  keys={keys}
                  readOnly={readOnly}
                />
                {!readOnly && (
                  <div>
                    <Button
                      size="sm"
                      color="danger"
                      outline
                      onClick={() => {
                        const newArr = [...value];
                        newArr.splice(idx, 1);
                        onChange(path, newArr);
                      }}
                    >
                      {t("Remove")}
                    </Button>
                  </div>
                )}

                {!readOnly && (
                  <div style={{ marginTop: 6 }}>
                    <Button
                      size="sm"
                      color="primary"
                      outline
                      onClick={() => {
                        const newArr = [...value, typeof value[0] === "object" ? {} : ""];
                        onChange(path, newArr);
                      }}
                    >
                      {t("Add")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  else {
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n");

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
        {useTextarea ? (
          <Input
            type="textarea"
            value={asString}
            rows={5}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        )}
      </FormGroup>
    );
  }
}


export const PaginationControl = ({ total, current, onChange }) => {
  const pages = getPagination(total, current);
  return (
    pages.length > 1 &&
    <Pagination>
      <PaginationItem disabled={current === 0}>
        <PaginationLink previous onClick={() => onChange(current - 1)} />
      </PaginationItem>
      {pages.map((p, i) => (
        <PaginationItem key={i} active={p === current} disabled={p === "..."}>
          <PaginationLink onClick={() => typeof p === "number" && onChange(p)}>{p === "..." ? "..." : p + 1}</PaginationLink>
        </PaginationItem>
      ))}
      <PaginationItem disabled={current === total - 1}>
        <PaginationLink next onClick={() => onChange(current + 1)} />
      </PaginationItem>
    </Pagination>
  );
};


/* ──────────────── Helpers ──────────────── */
export const getPagination = (total, current, maxVisible = 7) => {
  if (total <= 1) return [];
  const pages = [0];
  if (total <= maxVisible) return [...pages, ...Array.from({ length: total - 1 }, (_, i) => i + 1)];

  let start = Math.max(1, current - 2);
  let end = Math.min(total - 2, current + 2);
  if (end - start + 1 < maxVisible - 2) {
    current <= Math.floor(total / 2)
      ? (end = Math.min(total - 2, start + (maxVisible - 4)))
      : (start = Math.max(1, end - (maxVisible - 4)));
  }

  if (start > 1) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 2) pages.push("...");
  return [...pages, total - 1];
};

export function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

/* ──────────────── Main Component ──────────────── */
const CaseDetails = () => {
  const { t, i18n } = useTranslation();
  const { user } = getAuthUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state = {} } = useLocation();

  const [data, setData] = useState([]);
  const [caseIndex, setCaseIndex] = useState(0);
  const [judgementIndex, setJudgementIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("judgments");

  const [isSaving, setIsSaving] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showReviewed, setShowReviewed] = useState(false);
  const [paraLegalIDs, setParaLegalIDs] = useState(state.ParaLegalIDs || []);


  /* ─── Helpers ─── */
  const onChangeValue = (path, newVal) => {
    const updated = updateAtPath(data, path, newVal);
    setData(updated);
    if (user.RoleID === 118) debouncedSave(updated);
  };

  const handleSaveJson = async (payloadData, pushToPinecone = false, status = state.Status) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const payload = {
        DocID: state.DocID,
        JsonURL: state.JsonURL,
        StatusID: status,
        pushToPinecone: pushToPinecone,
        CaseJson: payloadData,
        UserID: user.ID,
      };

      // const res = await fetch(url.SAVE_CASE_DETAILS, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(payload),
      // });

      // if (!res.ok) throw new Error("Failed saving document");
      // const { url: newUrl } = await res.json();
      // state.JsonURL = newUrl;

      const response = await fetch(url.SAVE_CASE_DETAILS, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), 
      });

      if (!response.body) throw new Error("No response body for stream");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      // Process streaming response
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop(); // keep last incomplete chunk

        for (const eventBlock of events) {
          if (!eventBlock.trim()) continue;

          let eventName = "";
          let eventData = "";

          for (const line of eventBlock.split("\n")) {
            if (line.startsWith("event:"))
              eventName = line.slice(6).trim();
            else if (line.startsWith("data:"))
              eventData += line.slice(5).trim();
          }

          if (!eventName || !eventData) continue;

          try {
            const parsedData = JSON.parse(eventData);

            if (eventName === "file_saved") {
              // ✅ Update state safely
              if (state.JsonURL != parsedData.url) {
                state.JsonURL = parsedData.url;
                state.ParaLegalIDs = paraLegalIDs;
                state.Status = status;
            
                navigate('/ai/case-details', { 
                  state:  {
                    ...state
                  },
                  replace: true
                })
              } else {
                toast.success(t("DocumentDetailsSavedMessage"));
              }
              return parsedData;
            }
          } catch (err) {
            console.warn("Invalid SSE event data:", eventData, err);
          }
        }
      }

      toast.success(t("DocumentDetailsSavedMessage"));
    } catch (e) {
      console.log('Error: ', e);
      toast.error(t("SaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const debouncedSave = useCallback(debounce(handleSaveJson, 3000), []);

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = state.FileName?.replace(".pdf", ".json") || "document.json";
    link.click();
  };

  const handleReview = async () => {
    await handleSaveJson(data, true, 6);
    // dispatch(reviewedDocumentJson({ DocID: state.DocID, IsSaved: true }));
    setShowReviewed(false);
  };

  const onReassignClickHandler = (paraLegalIDs) => {
    state.Status = 5;
    state.ParaLegalIDs = paraLegalIDs;
    handleSaveJson(data, false, 5)
    const data = {
      CaseID: state.DocID,
      Status: 5,
      ParaLegalIDs: paraLegalIDs,
    }
    
    setParaLegalIDs(paraLegalIDs);
    dispatch(updateCaseJson(data))
    setShowAssign(false);
  }

  /* ─── Fetch JSON & Users ─── */
  useEffect(() => {
    (async () => {
      if (!state.JsonURL) return;
      // const res = await fetch("https://storage.googleapis.com/rag-legal-docs-dev/final_output_test/1761554720133.json");
      const res = await fetch(state.JsonURL);
      const jsonData = await res.json();
      setData(jsonData);
    })();
    dispatch(fetchUsers({ IsSystemUser: true, IsActive: true, RoleID: 118 }));
  }, [state.JsonURL]);

  const selectedCase = data[caseIndex] || { judgments: [{}] };

  const sectionTabs = [
    { id: "judgments", label: t("Judgments") },
    { id: "parties", label: t("Parties") },
    { id: "full_judgment_text", label: t("Full Judgement") },
    { id: "procedural_history", label: t("Procedural History") },
    { id: "principles", label: t("Principles") },
    { id: "rule_of_law", label: t("Rule Of Law") },
    { id: "text_segments", label: t("Text Segments") },
  ];

  /* ──────────────── UI ──────────────── */
  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <CardTitle tag="h4">{state.FileName}</CardTitle>

            {/* Top Action Bar */}
            <Row className="align-items-center mb-3">
              <Col md="6">
                <Button color="secondary" onClick={() => navigate(-1)}>
                  <i className={`ri-arrow-${i18n.language === "en" ? "left" : "right"}-line`} />
                </Button>
              </Col>
              <Col md="6" className="text-end gap-2">
                {[1, 4, 98].includes(user.RoleID) && (
                  <div className="d-flex justify-content-end gap-2">
                    <Button onClick={exportJSON}>{t("Export JSON")}</Button>
                    <Button color="success" disabled={isSaving} onClick={() => handleSaveJson(data)}>
                      {t("Save")}
                    </Button>
                    <Button disabled={isSaving} onClick={() => setShowAssign(true)}>
                      {t("Assign")}
                    </Button>
                  </div>
                )}
                {user.RoleID === 118 && (
                  <Button color="success" onClick={() => setShowReviewed(true)} disabled={isSaving}>
                    {t("Reviewed")}
                  </Button>
                )}
              </Col>
            </Row>

            <div className="mt-2 mb-2 d-flex flex-wrap gap-2">
              { data.map((item, index) => {
                return (
                  <Button
                    size="sm"
                    color={index == caseIndex ? "primary" : "secondary"}
                    outline={index != caseIndex}
                    onClick={() => {
                      setCaseIndex(index);
                      setJudgementIndex(0);
                    }}
                  >
                    {`${t('Case')} - ${index + 1}`}
                  </Button>
                )
              }) }
            </div>

            {/* Tabs */}
            <Nav tabs>
              {sectionTabs.map(tab => (
                <NavItem key={tab.id}>
                  <NavLink
                    className={activeTab === tab.id ? "active" : ""}
                    onClick={() => setActiveTab(tab.id)}
                    style={{ cursor: "pointer" }}
                  >
                    {tab.label}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>


            <TabContent activeTab={activeTab} className="mt-3">

              <div className="mt-2 mb-2 d-flex flex-wrap gap-2">
                { selectedCase?.judgments?.map((item, index) => {
                  return (
                    <Button
                      size="sm"
                      color={index == judgementIndex ? "primary" : "secondary"}
                      outline={index != judgementIndex}
                      onClick={() => setJudgementIndex(index)}
                    >
                      {`${t('Judgement')} - ${index + 1}`}
                    </Button>
                  )
                }) }
              </div>

              <TabPane tabId="judgments">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="judgments"
                      value={selectedCase?.judgments[judgementIndex]}
                      path={[caseIndex, "judgments", judgementIndex]}
                      onChange={onChangeValue} 
                      keys={hideFields.judgments}
                    />

                    {/* <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    /> */}
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="parties">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="parties"
                      value={selectedCase?.judgments[judgementIndex]?.parties}
                      path={[caseIndex, "judgments", judgementIndex, "parties"]}
                      onChange={onChangeValue} 
                    />

                    <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    />
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="full_judgment_text">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="full_judgment_text"
                      value={selectedCase?.judgments[judgementIndex]?.full_judgment_text}
                      path={[caseIndex, "judgments", judgementIndex, "full_judgment_text"]}
                      onChange={onChangeValue} 
                    />

                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="procedural_history">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="procedural_history"
                      value={selectedCase?.judgments[judgementIndex]?.procedural_history}
                      path={[caseIndex, "judgments", judgementIndex, "procedural_history"]}
                      onChange={onChangeValue} 
                    />

                    <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    />
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="principles">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="principles"
                      value={selectedCase?.judgments[judgementIndex]?.principles}
                      path={[caseIndex, "judgments", judgementIndex, "principles"]}
                      onChange={onChangeValue} 
                    />

                    <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    />
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="rule_of_law">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="rule_of_law"
                      value={selectedCase?.judgments[judgementIndex]?.rule_of_law}
                      path={[caseIndex, "judgments", judgementIndex, "rule_of_law"]}
                      onChange={onChangeValue} 
                    />

                    <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    />
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="text_segments">
                <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto", // vertical scrollbar for PDF
                    }}>

                    <RecursiveEditor
                      name="text_segments"
                      value={selectedCase?.judgments[judgementIndex]?.text_segments}
                      path={[caseIndex, "judgments", judgementIndex, "text_segments"]}
                      onChange={onChangeValue} 
                    />

                    <PaginationControl
                      total={selectedCase?.judgments?.length || 0}
                      current={judgementIndex}
                      onChange={p => setJudgementIndex(p)}
                    />
                    
                  </Col>
                  <Col md="6"
                    style={{
                      height: "100%",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}>
                    <iframe
                      src={state.FileURL || ''}
                      width="100%"
                      height="95%"
                      title="PDF Viewer"
                      style={{ minHeight: "570px" }}
                      ></iframe>
                  </Col>
                </Row>
              </TabPane>

            </TabContent>
          </CardBody>
        </Card>
      </Container>

      {/* Assign Modal */}
      <FileAssignModal
        show={showAssign}
        doc={state}
        setShow={setShowAssign}  
        onReassignClickHandler={onReassignClickHandler}
      />

      {/* Reviewed Modal */}
      <Modal isOpen={showReviewed} centered={true}>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t('areYouSure')}</h4>
              <p className="text-muted mx-4 mb-0">{t('Are you sure you want to review this document?')}</p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn w-sm btn-light"
              data-bs-dismiss="modal"
              onClick={() => setShowReviewed(false)}
            >
              {t("Close")}
            </button>
            <button
              type="button"
              className="btn w-sm btn-danger d-flex align-items-center"
              disabled={isSaving}
              onClick={handleReview}
            >
              {t('Yes, Reviewed it')}
            </button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default CaseDetails;
